import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Maximize2, MoveHorizontal } from 'lucide-react';
import type { AnalyticsApi, AnalyticsRange } from '../../api/analytics';
import type { AnalyticsSite } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import { useLoad } from '../../lib/useLoad';
import { BarList } from './charts/Charts';
import { exact, percent } from './analyticsFormat';
import { biggestDrop, medianDepth, rampAt, rampGradient } from './heatScale';
import type { InstallSection } from './installForm';

/**
 * Where people click, and how far down they get.
 *
 * Three answers to one question, in descending order of how much they can be
 * trusted:
 *
 *  1. What was CLICKED — a leaderboard of real elements. This is primary,
 *     because "1,840 people clicked Get a price" is a fact an operator can act
 *     on and check.
 *  2. How far people SCROLLED — one bar per tenth of the page, which is what
 *     says whether the thing at the bottom is ever seen.
 *  3. WHERE the clicks landed, painted over the page. This is last on purpose:
 *     a blob at 63% across and 1,180px down is not verifiable by eye, and it is
 *     the part most likely to mislead. It is offered, framed as an illustration,
 *     and never the only thing on screen.
 */
const DEVICES = [
  { key: 'desktop', label: 'Desktop', width: 1280 },
  { key: 'tablet', label: 'Tablet', width: 834 },
  { key: 'mobile', label: 'Mobile', width: 390 },
] as const;

type DeviceKey = (typeof DEVICES)[number]['key'];

export function HeatmapView({
  api,
  site,
  range,
  onClose,
  onOpenSettings,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  range: AnalyticsRange;
  onClose: () => void;
  /** Opens Install and settings on a section — both fixes this screen asks for live there. */
  onOpenSettings?: (section: InstallSection) => void;
}) {
  const [device, setDevice] = useState<DeviceKey>('desktop');
  const [chosen, setChosen] = useState<string | null>(null);
  const [backdrop, setBackdrop] = useState(true);
  const [fit, setFit] = useState<'width' | 'page'>('width');

  /*
   * The list column sticks BELOW the pinned header, and the header is not a
   * fixed height: its controls wrap onto a second row on a narrow window, and
   * the embed pads it less than the standalone route does. Measured rather than
   * guessed, because a guess is wrong at exactly the widths where a rail that
   * overlaps the header or floats under it looks broken.
   */
  const headRef = useRef<HTMLDivElement | null>(null);
  const [headHeight, setHeadHeight] = useState(92);
  useEffect(() => {
    const node = headRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    // The BORDER box: `contentRect` leaves out the header's own padding, and a
    // rail offset by the content height alone sits 33px too high.
    const observer = new ResizeObserver(([entry]) =>
      setHeadHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const pages = useLoad(() => api.getAnalyticsTop(range, 'page', 40), [range]);
  // Derived, not seeded from an effect: until someone picks a page this opens
  // on the busiest one, which on most sites is the homepage and on some is a
  // landing page nobody would have thought to look at.
  const path = chosen ?? pages.data?.payload?.[0]?.value ?? '/';
  const heat = useLoad(() => api.getAnalyticsHeatmap(range, path, device), [range, path, device]);

  const origin = site.allowedOrigins?.[0] ?? null;
  const pageUrl = origin ? `${origin.replace(/\/$/, '')}${path}` : null;
  const backdropWhyNot = pageUrl
    ? null
    : 'Add your site under Install and settings → Collection → Allowed sites to show the page behind these marks.';
  const cells = heat.data?.payload.cells ?? [];
  const scroll = heat.data?.payload.scroll ?? [];
  const targets = heat.data?.payload.targets ?? [];
  const median = medianDepth(scroll);
  const cliff = biggestDrop(scroll);

  return (
    <div className="an-heat" style={{ ['--an-head-h' as string]: `${Math.round(headHeight)}px` }}>
      <div className="an-head" ref={headRef}>
        {/* The way back beside the heading rather than stacked above it: this
            header is pinned, and three stacked rows of it is a third of the
            stage's height spent on chrome. */}
        <div className="an-heat-heading">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            title="Back to the dashboard"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 className="an-title">Clicks and scrolling</h1>
            <p className="an-sub">
              {site.name} · {path}
            </p>
          </div>
        </div>
        <div className="an-controls">
          <select className="select" value={path} onChange={(event) => setChosen(event.target.value)}>
            {(pages.data?.payload ?? []).map((row) => (
              <option key={row.value} value={row.value}>
                {row.value}
              </option>
            ))}
            {pages.data?.payload.some((row) => row.value === path) ? null : (
              <option value={path}>{path}</option>
            )}
          </select>
          <div className="an-seg" role="group" aria-label="Device">
            {DEVICES.map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={device === entry.key ? 'on' : ''}
                onClick={() => setDevice(entry.key)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          {heat.loading ? <Spinner /> : null}
        </div>
      </div>

      {heat.error ? <ErrorNote error={heat.error} /> : null}
      {heat.data?.unavailable ? (
        <Note tone="info">
          {heat.data.unavailable} Turn on <b>Clicks and scrolling</b> under Install and settings →
          Collection; it starts recording from that moment, so give it a few days before reading this.{' '}
          {onOpenSettings ? (
            <button type="button" className="an-link" onClick={() => onOpenSettings('collection')}>
              Open Collection
            </button>
          ) : null}
        </Note>
      ) : null}

      <div className="an-heat-cols">
        <div className="an-heat-side">
          <section className="an-card">
            <div className="an-card-head">
              <h2 className="an-card-title">What was clicked</h2>
            </div>
            {targets.length ? (
              <BarList
                rows={targets.map((row) => ({
                  label: row.label || row.selector,
                  value: row.clicks,
                  share: row.share,
                  color: 'var(--viz-1)',
                  meta: row.label ? row.selector : undefined,
                }))}
              />
            ) : (
              <div className="an-empty">No clicks recorded on this page yet.</div>
            )}
            <p className="an-caveat">
              Elements are identified by their position in the page, so a redesign starts them over.
              Give the ones you care about a <code>data-sa</code> attribute and they keep their
              identity — and their name here — across rebuilds.
            </p>
          </section>

          <section className="an-card">
            <div className="an-card-head">
              <h2 className="an-card-title">How far down they got</h2>
            </div>
            {scroll.length ? (
              <div className="an-scrolls">
                {scroll.map((row) => (
                  <div
                    className={`an-scroll-row${row.bucket === cliff ? ' is-cliff' : ''}${
                      row.bucket === median ? ' is-median' : ''
                    }`}
                    key={row.bucket}
                  >
                    <span className="an-scroll-label">{row.bucket}%</span>
                    <span className="an-bar-track">
                      <span
                        className="an-bar-fill"
                        style={{
                          width: `${Math.max(1, Math.round(row.share * 100))}%`,
                          background: 'var(--viz-3)',
                        }}
                      />
                    </span>
                    <span className="an-num an-scroll-value">{percent(row.share, 0)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="an-empty">Nothing recorded for this page yet.</div>
            )}
            {/* The caveat below asks the reader to find the cliff off ten bars,
                which is the part people get wrong. It is computed and marked
                instead — the rule sits at the drop, and the sentence names it. */}
            {scroll.length ? (
              <p className="an-scroll-read">
                {median
                  ? `Half of readers got at least ${median}% down.`
                  : 'Fewer than half got past the first tenth.'}{' '}
                {cliff ? `Most of the rest are lost between ${cliff - 10}% and ${cliff}%.` : null}
              </p>
            ) : null}
            <p className="an-caveat">
              Read as a share of everyone who opened the page. A cliff between two bands is where
              people stop — anything below it is written for an audience that mostly is not there.
            </p>
          </section>
        </div>

        <section className="an-card an-heat-main">
          <div className="an-card-head">
            <h2 className="an-card-title">Where the clicks landed</h2>
            <span className="an-card-head-end">
              {/* Off and unusable without an origin to frame, rather than on
                  and inert. An install with no Allowed origins still collects
                  perfectly — an empty allowlist accepts every beacon, which is
                  the documented way to start — so this is the one feature that
                  silently has nothing to work with, and a checked box over a
                  blank stage reads as the page having failed to load. The
                  reason travels with the control; the caveat under the stage
                  says the same thing, but after the fact. */}
              <label className="an-inline-check" title={backdropWhyNot ?? undefined}>
                <input
                  type="checkbox"
                  checked={backdrop && Boolean(pageUrl)}
                  disabled={!pageUrl}
                  onChange={(event) => setBackdrop(event.target.checked)}
                />
                Show the page behind it
              </label>
              {/* A page of any length renders here at the column's width, which
                  on a long one is several screens of scrolling before the shape
                  of the field is visible at all. */}
              <span className="an-seg" role="group" aria-label="Zoom">
                <button
                  type="button"
                  className={fit === 'width' ? 'on' : ''}
                  title="Fill the column, and scroll the page"
                  onClick={() => setFit('width')}
                >
                  <MoveHorizontal size={13} /> Fit width
                </button>
                <button
                  type="button"
                  className={fit === 'page' ? 'on' : ''}
                  title="Shrink until the whole page is in view"
                  onClick={() => setFit('page')}
                >
                  <Maximize2 size={13} /> Whole page
                </button>
              </span>
            </span>
          </div>
          {/* The stage is an illustration, so it says what it is an
              illustration OF: which address, at which width. */}
          <div className="an-heat-bar">
            <span className="an-heat-url" title={pageUrl ?? path}>
              {pageUrl ?? path}
            </span>
            <span>
              {exact(heat.data?.stats.clicks ?? 0)} clicks ·{' '}
              {DEVICES.find((entry) => entry.key === device)?.width}px wide
            </span>
            {/* In the bar rather than under the stage: a page is several screens
                tall here, and a key at the bottom of one is a key nobody
                reaches until after they have read the picture. */}
            <HeatLegend maxCell={heat.data?.stats.maxCell ?? 0} />
          </div>
          <HeatStage
            cells={cells}
            maxCell={heat.data?.stats.maxCell ?? 0}
            width={DEVICES.find((entry) => entry.key === device)?.width ?? 1280}
            pageUrl={backdrop ? pageUrl : null}
            fit={fit}
          />
          <p className="an-caveat">
            Drawn at the width this device group is recorded against, so the positions line up with
            the page as it is laid out here and not as it looks on your own screen.{' '}
            {pageUrl
              ? 'If the page behind stays blank, your site tells browsers not to let it be framed, which is a reasonable thing for it to do. The click list on the left does not depend on it.'
              : 'Add your site to Allowed sites under Install and settings → Collection and the page itself can be shown behind these marks.'}
          </p>
        </section>
      </div>
    </div>
  );
}

/**
 * The painted overlay.
 *
 * Two passes, which is the only way to get a real gradient rather than a pile of
 * coloured circles: pass one accumulates greyscale alpha so overlapping clicks
 * add up, pass two replaces every pixel with a colour looked up from that alpha.
 * Compositing coloured blobs directly with `lighter` shifts their hue wherever
 * they overlap, which is exactly where the map is supposed to be read.
 */
function HeatStage({
  cells,
  maxCell,
  width,
  pageUrl,
  fit,
}: {
  cells: { x: number; y: number; clicks: number }[];
  maxCell: number;
  width: number;
  pageUrl: string | null;
  /** `width` fills the column and scrolls; `page` shrinks until it all fits. */
  fit: 'width' | 'page';
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [column, setColumn] = useState({ width: 0, scale: 1 });
  // The whole-page fit is measured against the WINDOW, not the column: what it
  // is worth is seeing the shape of the field without scrolling, and that is a
  // question about the viewport the reader is looking through.
  const [viewport, setViewport] = useState(() =>
    typeof window === 'undefined' ? 800 : window.innerHeight,
  );
  useEffect(() => {
    const onResize = () => setViewport(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The page is 100px buckets, so the last bucket needs its own height plus a
  // little room or the bottom row is painted half off the stage.
  const height = useMemo(
    () => Math.max(600, cells.reduce((max, cell) => Math.max(max, cell.y), 0) + 240),
    [cells],
  );

  useEffect(() => {
    const node = hostRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const measured = entry.contentRect.width;
      if (measured > 0) setColumn({ width: measured, scale: Math.min(1, measured / width) });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [width]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d', { willReadFrequently: true });
    if (!canvas || !context) return;
    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);
    if (!cells.length || maxCell <= 0) return;

    // Pass one: alpha only.
    const radius = Math.max(28, Math.round(width / 22));
    for (const cell of cells) {
      const cx = (cell.x / 100) * width;
      const cy = cell.y;
      // Square-rooted for the same reason the traffic table is: click counts
      // are heavily skewed, and a linear ramp paints one hot spot and nothing
      // else.
      const weight = Math.min(1, Math.sqrt(cell.clicks / maxCell));
      const gradient = context.createRadialGradient(cx, cy, 0, cx, cy, radius);
      gradient.addColorStop(0, `rgba(0,0,0,${(weight * 0.85).toFixed(3)})`);
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      context.fillStyle = gradient;
      context.beginPath();
      context.arc(cx, cy, radius, 0, Math.PI * 2);
      context.fill();
    }

    // Pass two: alpha -> colour. Cool where it is quiet, warm where it is not.
    const image = context.getImageData(0, 0, width, height);
    const data = image.data;
    for (let index = 0; index < data.length; index += 4) {
      const alpha = data[index + 3];
      if (alpha === 0) continue;
      const t = alpha / 255;
      const [r, g, b] = rampAt(t);
      data[index] = r;
      data[index + 1] = g;
      data[index + 2] = b;
      data[index + 3] = Math.round(Math.min(215, alpha * 1.25));
    }
    context.putImageData(image, 0, 0);
  }, [cells, maxCell, width, height]);

  // 148px is the chrome above and below the stage on this screen; the floor
  // stops a short window from shrinking the page to a thumbnail nobody can read.
  const scale =
    fit === 'page' ? Math.min(column.scale, Math.max(260, viewport - 148) / height) : column.scale;

  return (
    <div
      ref={hostRef}
      className="an-heat-stage"
      style={{ height: height * scale }}
      data-fit={fit}
    >
      <div
        className="an-heat-inner"
        style={{
          width,
          height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          // Centred once it no longer fills the column, or the whole-page fit
          // sits against the left edge with a wide empty gutter beside it. The
          // measured width comes from the observer rather than from the ref,
          // which must not be read while rendering.
          marginLeft: fit === 'page' ? Math.max(0, column.width - width * scale) / 2 : 0,
        }}
      >
        {pageUrl ? (
          <iframe
            className="an-heat-frame"
            src={pageUrl}
            title="The page these clicks were recorded on"
            /*
             * The tenant's own page, rendered for reference only.
             *
             * `allow-scripts` WITHOUT `allow-same-origin` is the whole of what
             * is granted, and the pair is what makes it safe: the frame keeps
             * an opaque origin, so it cannot reach this console's DOM, cookies
             * or storage, and everything else stays off — no forms, no top
             * level navigation, no popups, no downloads.
             *
             * Scripts are on because without them this feature does not work on
             * a modern site. Animate-on-scroll libraries ship their elements at
             * `opacity: 0` in the served HTML and reveal them from JS: on the
             * install this was reported against, 92 of 195 inline styles were
             * `opacity:0`, so half the page stayed invisible behind the marks
             * and read as a page that had failed to load. Server-rendered text
             * is not enough when the site has deliberately hidden it.
             */
            sandbox="allow-scripts"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : null}
        <canvas ref={canvasRef} className="an-heat-canvas" />
      </div>
      {!cells.length ? (
        <div className="an-heat-blank">No clicks recorded on this page in this range.</div>
      ) : null}
    </div>
  );
}

/**
 * What the colours mean, drawn from the ramp the canvas paints so the two
 * cannot drift apart.
 */
function HeatLegend({ maxCell }: { maxCell: number }) {
  return (
    <div className="an-heat-legend">
      <span>Fewer clicks</span>
      <span className="an-heat-ramp" style={{ background: rampGradient() }} aria-hidden="true" />
      <span>{exact(maxCell)} in the busiest spot</span>
    </div>
  );
}
